/**
 * Pure, dependency-free logic for Linked Wheel + Map (SDD-24 / WL-052b).
 *
 * Mirrors the server's rules (yuga_wheel/linked_wheels/logic.py) so the FE gates
 * and prompts stay consistent with what the backend enforces. No React, no DOM,
 * no network — fully unit-tested in logic.test.js.
 *
 * The server is the source of truth (fail-closed); these helpers only drive UI
 * affordances (when to show the "keep exploring?" prompt, breadcrumb loop marks,
 * tombstone rendering) — never a substitute for the server check.
 */

// Gordon 2026-07-09 Q-18: soft cap at 10 with a "keep exploring?" prompt.
export const DRILLDOWN_SOFT_CAP = 10;
export const DRILLDOWN_HARD_CAP = 50;
export const DEFAULT_GRAPH_HOPS = 2;

/** True once the breadcrumb has descended SOFT_CAP levels — show the prompt. */
export function isSoftCapReached(depth) {
  return depth >= DRILLDOWN_SOFT_CAP;
}

/** Absolute ceiling; the UI stops offering "deeper" past this. */
export function isHardCapReached(depth) {
  return depth >= DRILLDOWN_HARD_CAP;
}

/** Would navigating to nextId revisit a wheel already on the breadcrumb path? */
export function pathFormsCycle(pathIds, nextId) {
  return Array.isArray(pathIds) && pathIds.includes(nextId);
}

/**
 * Push a wheel id onto the breadcrumb back-stack, capped at the hard cap so the
 * stack can never grow unbounded. Returns a NEW array (never mutates input).
 */
export function pushBreadcrumb(pathIds, nextId) {
  const base = Array.isArray(pathIds) ? pathIds.slice() : [];
  base.push(nextId);
  if (base.length > DRILLDOWN_HARD_CAP + 1) {
    return base.slice(base.length - (DRILLDOWN_HARD_CAP + 1));
  }
  return base;
}

/** Climb out to a given index in the breadcrumb (inclusive). */
export function climbTo(pathIds, index) {
  if (!Array.isArray(pathIds) || index < 0) return [];
  return pathIds.slice(0, index + 1);
}

/**
 * A link target is a "tombstone" (removed / now-private) when the server marks
 * it visibility_ok:false OR omits its name. Never render a name/thumbnail then.
 */
export function isTombstone(target) {
  if (!target || typeof target !== "object") return true;
  if (target.visibility_ok === false) return true;
  return !target.name && !target.thumbnail;
}

/** Clamp a requested hop count into the allowed 1..MAX range for the map. */
export function clampHops(hops, max = 4) {
  const n = Number.parseInt(hops, 10);
  if (Number.isNaN(n)) return DEFAULT_GRAPH_HOPS;
  return Math.max(1, Math.min(n, max));
}

/**
 * Ownership-filter predicate for the map (L4): given the active toggles
 * ({ mine, others, followers }) decide whether a node/edge is shown. An edge is
 * "mine" when the center owns the source; "other" otherwise. Kept pure so the
 * legend + filter row are testable.
 */
export function nodeVisibleUnderFilters(ownership, filters) {
  const f = filters || {};
  if (ownership === "mine") return f.mine !== false;
  if (ownership === "followed") return f.followers === true || f.others !== false;
  // "other"
  return f.others !== false;
}

/**
 * Group a parsed creator graph ({ center, nodes, edges }) into the clusters of
 * wheels that are LINKED TO EACH OTHER — the data the avatar-dropdown "Linked
 * wheels" section renders (Gordon 2026-07-20). Pure so the grouping is testable
 * without a browser or network.
 *
 * Rules:
 *   - Consider every non-tombstone wheel we know about (center + nodes). A
 *     tombstone (removed/private) never leaks a name/thumbnail, so it is dropped.
 *   - Build an undirected adjacency from edges (link direction is irrelevant to
 *     "these are connected"); a reciprocal edge is still one connection.
 *   - Return each connected COMPONENT with >= 2 members (a lone wheel with no
 *     links is not "linked to each other" and is excluded).
 *   - Deterministic: components keep first-seen order (center first, then node
 *     order); members inside a component are sorted by name then id so the list
 *     is stable across renders.
 *
 * @param {{center?: object, nodes?: object[], edges?: object[]}} graph
 * @returns {Array<Array<{id, name, portrait}>>}
 */
export function groupLinkedWheels(graph) {
  if (!graph || typeof graph !== "object") return [];

  const order = [];
  const byId = new Map();
  const add = (n) => {
    if (!n || n.id == null || n.tombstone) return;
    if (byId.has(n.id)) return;
    const wheel = {
      id: n.id,
      name: n.name || `Wheel ${n.id}`,
      portrait: n.portrait || null,
    };
    byId.set(n.id, wheel);
    order.push(n.id);
  };
  if (graph.center) add(graph.center);
  (Array.isArray(graph.nodes) ? graph.nodes : []).forEach(add);

  const adj = new Map();
  const connect = (a, b) => {
    if (a == null || b == null || a === b) return;
    if (!byId.has(a) || !byId.has(b)) return; // ignore edges to unknown/dropped wheels
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a).add(b);
    adj.get(b).add(a);
  };
  (Array.isArray(graph.edges) ? graph.edges : []).forEach((e) =>
    connect(e && e.source, e && e.target)
  );

  const visited = new Set();
  const groups = [];
  for (const startId of order) {
    if (visited.has(startId) || !adj.has(startId)) continue; // skip unlinked wheels
    const stack = [startId];
    visited.add(startId);
    const comp = [];
    while (stack.length) {
      const cur = stack.pop();
      comp.push(byId.get(cur));
      for (const nb of adj.get(cur) || []) {
        if (!visited.has(nb)) {
          visited.add(nb);
          stack.push(nb);
        }
      }
    }
    if (comp.length >= 2) {
      comp.sort(
        (a, b) =>
          String(a.name).localeCompare(String(b.name)) || Number(a.id) - Number(b.id)
      );
      groups.push(comp);
    }
  }
  return groups;
}

/** Total linked-wheel count across all groups (drives the section's "N" hint / hide). */
export function countLinkedWheels(groups) {
  if (!Array.isArray(groups)) return 0;
  return groups.reduce((sum, g) => sum + (Array.isArray(g) ? g.length : 0), 0);
}
